import { useCallback, useMemo, useState } from "react";

import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import {
  type SignatureCaptureMethod,
  isSignatureCaptureMethod
} from "./providers/signature-provider.type";

/** Bump the version suffix to reset everyone's remembered choice. */
export const SIGNATURE_METHOD_STORAGE_KEY = "suno_signature_capture_method_v1";

export const DEFAULT_SIGNATURE_METHOD: SignatureCaptureMethod = "canvas";

interface StoredSignatureMethod {
  method: SignatureCaptureMethod;
}

function isStoredSignatureMethod(data: unknown): data is StoredSignatureMethod {
  return isSignatureCaptureMethod((data as StoredSignatureMethod)?.method);
}

/**
 * Remembers the signing method for THIS browser/workstation.
 *
 * Deliberately localStorage rather than a per-clinic backend setting: a Topaz
 * pad is physically attached to one workstation, so a clinic-level flag would
 * be the wrong granularity (it would tell a roaming laptop it has a pad). This
 * also keeps the whole feature frontend-only — no migration, no serializer
 * field, and no new write key to break FE-first deploys.
 */
export function useSignatureMethod(): {
  method: SignatureCaptureMethod;
  setMethod: (method: SignatureCaptureMethod) => void;
} {
  const storage = useMemo(
    () => setupLocalStorageAPI(SIGNATURE_METHOD_STORAGE_KEY),
    []
  );

  const [method, setMethodState] = useState<SignatureCaptureMethod>(() => {
    // A stale or hand-edited value must never wedge the signing dialog —
    // getData's validator falls back to the canvas default.
    const stored = storage.getData(null, isStoredSignatureMethod);
    return isStoredSignatureMethod(stored)
      ? stored.method
      : DEFAULT_SIGNATURE_METHOD;
  });

  const setMethod = useCallback(
    (next: SignatureCaptureMethod) => {
      setMethodState(next);
      storage.updateData({ method: next });
    },
    [storage]
  );

  return { method, setMethod };
}
